import React, { Suspense, useState, useCallback, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { useMapState } from './hooks/useMapState';
import { useVisibilityState } from './hooks/useVisibilityState';
import { useMapInitialization } from './hooks/useMapInitialization';
import { useDirections } from './hooks/useDirections';
import { usePOISelection } from './hooks/usePOISelection';
import { useMapBounds } from './hooks/useMapBounds';
import { useSurvey } from '@contexts/SurveyContext';
import { useLocationTracking } from './hooks/useLocationTracking';
import { usePOIManager } from '@hooks/map/usePOIManager';
import { useUserSettings } from '@hooks/useUserSettings';
import { MapHeader } from './MapHeader';
import { MapSidebar } from './sidebar/MapSidebar';
import { MapControls } from './MapControls';
import { POIManager } from './POIManager';
import { StatusBar } from './StatusBar';
import { UserLocationMarker } from './UserLocationMarker';
import { LoadingSpinner } from '@common/LoadingSpinner';
import { MapErrorBoundary } from './MapErrorBoundary'; 
import { POISelector } from '../poi/POISelector';
import { POIForm } from '../poi/POIForm';
import { Toast } from '@common/Toast';
import { RoutePlanner } from './RoutePlanner';
import { RecordingStatsModal } from '../status/RecordingStatsModal';
import { LocationInfoModal } from '../status/LocationInfoModal';
import { DatabaseStatusModal } from '../status/DatabaseStatusModal';
import { BottomToolbar } from './BottomToolbar';
import { FileDropZone } from './FileDropZone';
import GoogleMap from './GoogleMap';
import { logError, logInfo } from '@utils/logger'; 
import { doc, getDoc, deleteDoc, updateDoc } from 'firebase/firestore';
import { db } from '@config/firebase';
import { COLLECTIONS } from '@utils/database/collections';
import { AlertTriangle } from 'lucide-react';

const DEFAULT_CENTER = { lat: 45.5017, lng: -73.5673 };

// Import styles
import './styles.css';

const MapContainer = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { activeSurvey } = useSurvey();
  const { userLocation, locationStatus, isLocked, hasSignal, toggleLock } = useLocationTracking();
  const { settings } = useUserSettings();
  const { pois = [], routes = [], loading: dataLoading } = usePOIManager(location.state?.surveyId || activeSurvey?.id);
  const [toast, setToast] = useState(null);
  const [mapError, setMapError] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [showRecordingStats, setShowRecordingStats] = useState(false);
  const [showLocationInfo, setShowLocationInfo] = useState(false);
  const [showDatabaseStatus, setShowDatabaseStatus] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState(null);

  // Custom hooks
  const { mapState, handleMapTypeChange, handleModeChange } = useMapState(settings);
  const { visibilityState, handleVisibilityToggle } = useVisibilityState(settings);
  const { map, mapLoaded, handleMapLoad } = useMapInitialization();
  const { directionsService, directionsRenderer, handleDriveTo } = useDirections(map);
  const { 
    showPOISelector, 
    selectedLocation, 
    selectedFile, 
    showFilePOIForm, 
    handleMapClick, 
    resetSelection,
    setSelectedLocation: updateSelectedLocation,
    setShowPOISelector: updateShowPOISelector,
    setSelectedFile: updateSelectedFile,
    setShowFilePOIForm: updateShowFilePOIForm
  } = usePOISelection();
  const { calculateBounds } = useMapBounds(map, pois, routes);

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Handle pending POI placement
  useEffect(() => {
    const loadPendingPOI = async () => {
      try {
        // Validate location state
        if (!location.state?.placePOI) {
          logInfo('No pending POI to load');
          return null;
        }

        const poiId = location.state.placePOI;
        logInfo(`Loading pending POI: ${poiId}`);

        // Get POI document
        const poiRef = doc(db, COLLECTIONS.POIS, poiId);
        const poiDoc = await getDoc(poiRef);
        
        if (poiDoc.exists()) {
          const poiData = {
            id: poiDoc.id,
            ...poiDoc.data()
          };
          
          // Update UI state for POI placement
          handleModeChange('selection');
          setToast({
            type: 'info',
            message: 'Click on the map to place the POI'
          });
          
          logInfo('Pending POI loaded successfully');
          return poiData;
        } else {
          throw new Error('POI document not found');
        }
      } catch (error) {
        const errorMessage = error.code === 'permission-denied' 
          ? 'You do not have permission to access this POI'
          : 'Failed to load POI data';
        
        logError(`Failed to load pending POI: ${error.message}`, error);
        
        setToast({
          type: 'error',
          message: errorMessage
        });

        // Reset location state to prevent further attempts
        navigate(location.pathname, { 
          replace: true,
          state: { ...location.state, placePOI: null }
        });
      }
    };

    loadPendingPOI();
  }, [location.state?.placePOI, handleModeChange, navigate, location.pathname, location.state]);

  // File drop handler
  const handleFileDrop = useCallback((file) => {
    updateSelectedFile(file);
    handleModeChange('selection');
    setToast({
      type: 'info',
      message: 'Click on the map to place the file POI'
    });
  }, [handleModeChange]);

  // POI creation handlers
  const handleCurrentLocationPOI = useCallback(() => {
    if (!userLocation) return;
    
    updateSelectedLocation({
      lat: userLocation.lat,
      lng: userLocation.lng
    });
    updateShowPOISelector(true);
  }, [userLocation, updateSelectedLocation, updateShowPOISelector]);

  const handleMapClickPOI = useCallback(() => {
    handleModeChange('selection');
    setToast({
      type: 'info',
      message: 'Click on the map to add a POI'
    });
  }, [handleModeChange, setToast]);

  // Handle POI deletion
  const handleDeletePOI = useCallback(async (poiId) => {
    // Show confirmation toast instead of system dialog
    setDeleteConfirmation({
      poiId,
      message: 'Are you sure you want to delete this POI?'
    });
  }, []);

  // Confirm POI deletion
  const confirmDeletePOI = useCallback(async () => {
    if (!deleteConfirmation?.poiId) return;
    
    try {
      await deleteDoc(doc(db, COLLECTIONS.POIS, deleteConfirmation.poiId));
      setToast({
        type: 'success',
        message: 'POI deleted successfully'
      });
    } catch (error) {
      logError('Failed to delete POI:', error);
      setToast({
        type: 'error',
        message: 'Failed to delete POI'
      });
    } finally {
      setDeleteConfirmation(null);
    }
  }, [deleteConfirmation]);

  // Cancel POI deletion
  const cancelDeletePOI = useCallback(() => {
    setDeleteConfirmation(null);
  }, []);

  // Update map center when location changes and locked
  useEffect(() => {
    if (map && userLocation && isLocked) {
      map.panTo(userLocation);
    }
  }, [map, userLocation, isLocked]);

  return (
    <MapErrorBoundary>
      <Suspense fallback={<LoadingSpinner />}>
        <div className="h-screen w-full relative bg-[#1a1a1a] overflow-hidden">
          {dataLoading && (
            <div className="absolute inset-0 bg-black/50 flex items-center justify-center z-50">
              <LoadingSpinner />
            </div>
          )}
          {mapError && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/80 z-50">
              <div className="bg-[#2A2B2E] p-6 rounded-lg text-center max-w-md border-2 border-red-500">
                <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-red-500 mb-2">Map Error</h3>
                <p className="text-red-400">{mapError}</p>
                <button
                  onClick={() => window.location.reload()}
                  className="mt-4 px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600"
                >
                  Reload Page
                </button>
              </div>
            </div>
          )}
          {/* Header */}
          <MapHeader 
            onBack={() => navigate(-1)} 
            survey={activeSurvey} 
          />

          {/* Main Map */}
          <div className="h-[calc(100vh-64px)] w-full relative mt-16 overflow-hidden">
            
            <GoogleMap
              onMapLoad={handleMapLoad}
              center={location.state?.center || userLocation || DEFAULT_CENTER}
              zoom={activeSurvey ? 10 : 12}
              options={{
                draggable: !isLocked,
                scrollwheel: !isLocked,
                gestureHandling: isLocked ? 'none' : 'cooperative'
              }}
              mapType={mapState.mapType}
              mode={mapState.mode}
              onClick={handleMapClick}
              showTraffic={visibilityState.showTraffic}
              showStreetView={visibilityState.showStreetView}
              onError={(error) => setMapError(error.message)}
            >
              {userLocation && (
                <UserLocationMarker 
                  position={userLocation}
                  hasSignal={hasSignal}
                  isLocked={isLocked}
                />
              )}
              {mapLoaded && map && activeSurvey && (
                <POIManager
                  map={map}
                  pois={pois}
                  routes={routes}
                  onDriveTo={handleDriveTo}
                  onDelete={handleDeletePOI}
                  visibilityState={visibilityState}
                  settings={settings?.preferences?.mapSettings}
                />
              )}
            </GoogleMap>
          </div>

          {/* Overlays */}
          <FileDropZone onFileDrop={handleFileDrop} />
          <MapSidebar
            map={map}
            onVisibilityChange={handleVisibilityToggle}
            visibilityState={visibilityState}
            defaultCollapsed={isMobile}
            isLocked={isLocked}
            onToggleLock={toggleLock}
            className="z-[150] pointer-events-auto"
            style={{ transform: 'translateZ(0)' }}
          />
          <MapControls
            map={map}
            onMapTypeChange={handleMapTypeChange}
            onModeChange={handleModeChange}
            isLocked={isLocked}
            onToggleLock={toggleLock}
            mapType={mapState.mapType}
            mode={mapState.mode}
            visibilityState={visibilityState}
            onVisibilityChange={handleVisibilityToggle}
            className="left-4 md:left-6"
            defaultCollapsed={isMobile}
          />
          <BottomToolbar
            userLocation={userLocation}
            onCurrentLocationPOI={handleCurrentLocationPOI}
            onMapClickPOI={handleMapClickPOI}
          />

          {/* Modals */}
          {showPOISelector && selectedLocation && (
            <POISelector
              location={selectedLocation}
              onClose={resetSelection}
            />
          )}
          {showFilePOIForm && selectedLocation && selectedFile && (
            <POIForm
              type="File"
              coordinates={selectedLocation}
              file={selectedFile}
              onClose={resetSelection}
            />
          )}
          {mapState.showRoutePlanner && map && (
            <RoutePlanner
              map={map}
              activeSurvey={activeSurvey}
              onClose={() => handleModeChange('pan')}
            />
          )}
          {/* Status Modals */}
          <RecordingStatsModal
            isOpen={showRecordingStats}
            onClose={() => setShowRecordingStats(false)}
          />
          <LocationInfoModal
            isOpen={showLocationInfo}
            onClose={() => setShowLocationInfo(false)}
          />
          <DatabaseStatusModal
            isOpen={showDatabaseStatus}
            onClose={() => setShowDatabaseStatus(false)}
          />
          {toast && (
            <Toast
              message={toast.message}
              type={toast.type}
              onClose={() => setToast(null)}
            />
          )}
          
          {/* Delete Confirmation Toast */}
          {deleteConfirmation && (
            <div className="fixed bottom-20 left-1/2 transform -translate-x-1/2 bg-[#2A2B2E] text-white p-4 rounded-lg shadow-lg z-[200] w-[90%] max-w-md">
              <div className="flex items-start gap-3 mb-3">
                <AlertTriangle className="w-5 h-5 text-yellow-500 shrink-0 mt-0.5" />
                <p>{deleteConfirmation.message}</p>
              </div>
              <div className="flex justify-end gap-3">
                <button 
                  onClick={cancelDeletePOI}
                  className="px-3 py-1.5 bg-gray-700 hover:bg-gray-600 text-white rounded-lg"
                >
                  Cancel
                </button>
                <button 
                  onClick={confirmDeletePOI}
                  className="px-3 py-1.5 bg-red-600 hover:bg-red-700 text-white rounded-lg"
                >
                  Delete
                </button>
              </div>
            </div>
          )}
        </div>
      </Suspense>
    </MapErrorBoundary>
  );
};

export default MapContainer;

export { MapContainer }